import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import type { operations } from '../api/modelos'

/**
 * Los estados salen del documento OpenAPI, no de una lista escrita a mano: son
 * exactamente los que el endpoint acepta, asi que es imposible ofrecer en el
 * desplegable una opcion que devuelva 422.
 */
type ConsultaAvisos = NonNullable<operations['listar_avisos_api_avisos_get']['parameters']['query']>
export type EstadoAviso = NonNullable<ConsultaAvisos['estado']>

export const ESTADOS: readonly EstadoAviso[] = ['pendiente', 'avisado']

export interface FiltrosAvisos {
  estado?: EstadoAviso
  semanas_minimas?: number
  q: string
  page: number
}

type ClaveFiltro = 'estado' | 'semanas_minimas' | 'q'

// Igual que en logistica: el buscador se dispara con cada pulsacion (con un
// rebote de 300 ms) y apilaria una entrada de historial por letra, de modo que
// deshacer una busqueda costaria tantos "atras" como caracteres. `estado` y
// `semanas_minimas` son acciones discretas y cada una merece la suya.
const CLAVES_QUE_SUSTITUYEN_HISTORIAL: ReadonlySet<ClaveFiltro> = new Set(['q'])

function leerPagina(valor: string | null): number {
  const numero = Number.parseInt(valor ?? '', 10)
  return Number.isFinite(numero) && numero >= 1 ? numero : 1
}

/**
 * Un estado que no existe se ignora en lugar de viajar a la api. Sin esto,
 * `?estado=aplazado` (una url editada a mano o un enlace viejo) devolveria un
 * 422 y pintaria la pantalla entera de error por un parametro que basta con no
 * mirar.
 */
function leerEstado(valor: string | null): EstadoAviso | undefined {
  return ESTADOS.find((estado) => estado === valor)
}

/** Igual de estricto con el numero: cero es valido, un negativo o un texto no. */
function leerSemanas(valor: string | null): number | undefined {
  if (valor === null) return undefined
  const numero = Number.parseInt(valor, 10)
  return Number.isFinite(numero) && numero >= 0 ? numero : undefined
}

export function useFiltrosAvisos() {
  const [parametros, setParametros] = useSearchParams()

  const filtros = useMemo<FiltrosAvisos>(
    () => ({
      estado: leerEstado(parametros.get('estado')),
      semanas_minimas: leerSemanas(parametros.get('semanas_minimas')),
      q: parametros.get('q') ?? '',
      page: leerPagina(parametros.get('page')),
    }),
    [parametros],
  )

  const cambiarFiltro = useCallback(
    (clave: ClaveFiltro, valor: string | undefined) => {
      const siguientes = new URLSearchParams(parametros)
      // `valor` puede ser "0" en `semanas_minimas`, que es un filtro legitimo:
      // por eso se compara contra vacio y no se usa la veracidad de la cadena.
      if (valor !== undefined && valor !== '') siguientes.set(clave, valor)
      else siguientes.delete(clave)
      // Cambiar un filtro con la pagina 5 activa dejaria al usuario mirando una
      // pagina que quiza ya no existe en el resultado nuevo.
      siguientes.delete('page')
      setParametros(siguientes, { replace: CLAVES_QUE_SUSTITUYEN_HISTORIAL.has(clave) })
    },
    [parametros, setParametros],
  )

  const irAPagina = useCallback(
    (pagina: number) => {
      const siguientes = new URLSearchParams(parametros)
      if (pagina <= 1) siguientes.delete('page')
      else siguientes.set('page', String(pagina))
      setParametros(siguientes, { replace: false })
    },
    [parametros, setParametros],
  )

  return { filtros, cambiarFiltro, irAPagina }
}
